import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { STATUS_LABELS } from '../../lib/constants';
import ProfessionalLayout from '../../components/ProfessionalLayout';
import '../../styles/professional-theme.css';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Package, Ship, Users, TrendingUp, Clock } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { useLanguage } from '../../contexts/LanguageContext';
import SEOHead from '../../components/SEOHead';
import PaymentMethodChart from '../../components/finance/PaymentMethodChart';

const CHART_PALETTE = ['#1a1a1a', '#e8590c', '#b0b0b0', '#f0976c', '#666666', '#d4d4d4'];

const AnalyticsDashboard = () => {
  const { t, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [partnersPerformance, setPartnersPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('30');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [statsRes, partnersRes] = await Promise.all([
        api.get('/api/analytics/company-stats'),
        api.get('/api/analytics/all-partners-performance')
      ]);
      setStats(statsRes.data);
      setPartnersPerformance(partnersRes.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(language === 'en' ? 'Error loading statistics' : 'Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="analytics-loading">
          <div className="analytics-skeleton" style={{ height: 32, width: 280, marginBottom: 12 }} />
          <div className="analytics-skeleton" style={{ height: 20, width: 400, marginBottom: 40 }} />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {[1,2,3,4].map(i => (
              <div key={i} className="analytics-skeleton" style={{ height: 120, borderRadius: 8 }} />
            ))}
          </div>
        </div>
      </ProfessionalLayout>
    );
  }

  if (error) {
    return (
      <ProfessionalLayout>
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </ProfessionalLayout>
    );
  }

  const totalPkgs = stats?.total_packages || 0;
  const totalShipments = stats?.total_shipments || 0;
  const totalPartners = stats?.total_partners || 0;
  const inTransit = stats?.in_transit_packages || 0;
  const delivered = stats?.delivered_packages || 0;

  const weekNum = Math.ceil((new Date().getTime() - new Date(new Date().getFullYear(), 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000));

  // Chart data
  const statusData = stats?.package_status_distribution
    ? Object.entries(stats.package_status_distribution).map(([status, count]) => ({
        name: (STATUS_LABELS[language] || STATUS_LABELS.fr)[status] || status,
        value: count,
      }))
    : [];

  const shipmentsData = stats?.shipment_status_distribution
    ? Object.entries(stats.shipment_status_distribution).map(([status, count]) => ({
        name: status,
        value: count
      }))
    : [];

  const partnersData = partnersPerformance.map(p => ({
    name: p.partner_name?.split(' ')[0] || p.partner_name,
    total: p.total_packages,
    delivered: p.delivered_packages,
  }));

  const deliveryTimeData = partnersPerformance
    .filter(p => p.average_delivery_days > 0)
    .map(p => ({
      name: p.partner_name?.split(' ')[0],
      days: p.average_delivery_days
    }));

  const periods = [
    { value: '7', label: language === 'en' ? '7 days' : '7 jours' },
    { value: '30', label: language === 'en' ? '30 days' : '30 jours' },
    { value: '90', label: language === 'en' ? '90 days' : '90 jours' },
    { value: '365', label: language === 'en' ? 'Year' : 'Année' },
  ];

  const kpis = [
    { label: t('packages'), value: totalPkgs, delta: `${delivered} ${language === 'en' ? 'delivered' : 'livrés'}`, iconBg: 'background:rgb(232 89 12 / 0.15);color:#e8590c', icon: Package, cardBg: 'linear-gradient(135deg, #fff4ed 0%, #ffe8d6 100%)', cardBorder: '#f5c9a8' },
    { label: t('shipments'), value: totalShipments, delta: language === 'en' ? 'organized trips' : 'voyages organisés', iconBg: 'background:rgb(59 130 246 / 0.15);color:#3b82f6', icon: Ship, cardBg: 'linear-gradient(135deg, #f0f4ff 0%, #e8ecf4 100%)', cardBorder: '#c7d2e8' },
    { label: language === 'en' ? 'Partners' : 'Partenaires', value: totalPartners, delta: language === 'en' ? 'active' : 'actifs', iconBg: 'background:rgb(139 92 246 / 0.15);color:#8b5cf6', icon: Users, cardBg: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', cardBorder: '#c4b5fd' },
    { label: language === 'en' ? 'In Transit' : 'En transit', value: inTransit, delta: language === 'en' ? 'in progress' : 'en cours', iconBg: 'background:rgb(34 197 94 / 0.15);color:#22c55e', icon: TrendingUp, cardBg: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', cardBorder: '#86efac' },
  ];

  return (
    <ProfessionalLayout>
      <SEOHead title={t('seoDashboardTitle')} description={t('seoDashboardDesc')} noindex />
      {/* Page Header */}
      <div className="analytics-header">
        <div className="analytics-header-top">
          <span className="analytics-label">{language === 'en' ? `STATISTICS · WEEK ${weekNum}` : `STATISTIQUES · SEMAINE ${weekNum}`}</span>
        </div>
        <h1 className="analytics-title">
          {language === 'en'
            ? <><strong>{totalPkgs}</strong> packages processed, <strong>{totalShipments}</strong> departures organized</>
            : <><strong>{totalPkgs}</strong> colis traités, <strong>{totalShipments}</strong> départs organisés</>
          }
        </h1>
        <div className="analytics-header-row">
          <span className="analytics-update">
            <Clock size={13} />
            {language === 'en' ? 'Just updated' : 'Mis à jour à l\'instant'}
          </span>
          <div className="analytics-periods">
            {periods.map(p => (
              <button
                key={p.value}
                className={`analytics-period-btn ${period === p.value ? 'analytics-period-btn--active' : ''}`}
                onClick={() => setPeriod(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" style={{ marginBottom: 32 }}>
        {kpis.map((kpi) => (
          <div key={kpi.label} className="analytics-kpi" style={{ background: kpi.cardBg, borderColor: kpi.cardBorder }}>
            <div className="analytics-kpi-header">
              <span className="analytics-kpi-label">{kpi.label}</span>
              <div className="analytics-kpi-icon" style={Object.fromEntries(kpi.iconBg.split(';').map(s => { const [k,v] = s.split(':'); return [k.trim(), v.trim()]; }))}>
                <kpi.icon size={18} />
              </div>
            </div>
            <div className="analytics-kpi-value">{kpi.value}</div>
            <div className="analytics-kpi-delta">{kpi.delta}</div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" style={{ marginBottom: 32 }}>
        {/* Donut - Status Distribution */}
        <Card className="analytics-card">
          <CardHeader className="analytics-card-header">
            <CardTitle className="analytics-card-title">{language === 'en' ? 'Status Distribution' : 'Répartition des statuts'}</CardTitle>
          </CardHeader>
          <CardContent>
            {statusData.length > 0 ? (
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                <div className="w-full sm:w-1/2">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={85}
                        paddingAngle={2}
                        dataKey="value"
                        strokeWidth={0}
                      >
                        {statusData.map((_, index) => (
                          <Cell key={index} fill={CHART_PALETTE[index % CHART_PALETTE.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ fontSize: 13, borderRadius: 6, border: '1px solid #e0e0e0' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="analytics-legend w-full sm:w-1/2">
                  {statusData.map((entry, i) => (
                    <div key={i} className="analytics-legend-item">
                      <span className="analytics-legend-dot" style={{ background: CHART_PALETTE[i % CHART_PALETTE.length] }} />
                      <span className="analytics-legend-label">{entry.name}</span>
                      <span className="analytics-legend-value">{entry.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="analytics-empty">{t('noData')}</div>
            )}
          </CardContent>
        </Card>

        {/* Bar - Shipment Status */}
        <Card className="analytics-card">
          <CardHeader className="analytics-card-header">
            <CardTitle className="analytics-card-title">{language === 'en' ? 'Departure Status' : 'Statut des départs'}</CardTitle>
          </CardHeader>
          <CardContent>
            {shipmentsData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={shipmentsData} barSize={28}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#888' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: '#888' }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ fontSize: 13, borderRadius: 6, border: '1px solid #e0e0e0' }} />
                  <Bar dataKey="value" fill="#1a1a1a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="analytics-empty">{language === 'en' ? 'No departures created' : 'Aucun départ créé'}</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Partner Performance */}
      {partnersData.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" style={{ marginBottom: 32 }}>
          <Card className="analytics-card">
            <CardHeader className="analytics-card-header">
              <CardTitle className="analytics-card-title">{language === 'en' ? 'Partner Performance' : 'Performance partenaires'}</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={partnersData} barSize={16} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#888' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: '#888' }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ fontSize: 13, borderRadius: 6, border: '1px solid #e0e0e0' }} />
                  <Bar dataKey="total" fill="#1a1a1a" radius={[3, 3, 0, 0]} name="Total" />
                  <Bar dataKey="delivered" fill="#e8590c" radius={[3, 3, 0, 0]} name={language === 'en' ? 'Delivered' : 'Livrés'} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {deliveryTimeData.length > 0 && (
            <Card className="analytics-card">
              <CardHeader className="analytics-card-header">
                <CardTitle className="analytics-card-title">{language === 'en' ? 'Average Delivery Time' : 'Délais moyens de livraison'}</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={deliveryTimeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#888' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: '#888' }} axisLine={false} tickLine={false} unit={language === 'en' ? 'd' : 'j'} />
                    <Tooltip contentStyle={{ fontSize: 13, borderRadius: 6, border: '1px solid #e0e0e0' }} />
                    <Line
                      type="monotone"
                      dataKey="days"
                      stroke="#e8590c"
                      strokeWidth={2}
                      dot={{ r: 4, fill: '#e8590c', strokeWidth: 0 }}
                      name={language === 'en' ? 'Delay (days)' : 'Délai (jours)'}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Partner Table */}
      {partnersPerformance.length > 0 && (
        <Card className="analytics-card">
          <CardHeader className="analytics-card-header">
            <CardTitle className="analytics-card-title">{language === 'en' ? 'Partner Details' : 'Détails par partenaire'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>{language === 'en' ? 'Partner' : 'Partenaire'}</th>
                    <th>Total</th>
                    <th>{language === 'en' ? 'Delivered' : 'Livrés'}</th>
                    <th>{language === 'en' ? 'Rate' : 'Taux'}</th>
                    <th>{language === 'en' ? 'Avg. Delay' : 'Délai moyen'}</th>
                  </tr>
                </thead>
                <tbody>
                  {partnersPerformance.map((partner, index) => (
                    <tr key={index}>
                      <td>
                        <div className="analytics-table-partner">
                          <span className="analytics-table-name">{partner.partner_name}</span>
                          <span className="analytics-table-email">{partner.partner_email}</span>
                        </div>
                      </td>
                      <td><Badge variant="outline">{partner.total_packages}</Badge></td>
                      <td><Badge className="analytics-badge-success">{partner.delivered_packages}</Badge></td>
                      <td>
                        <Badge className={
                          partner.delivery_rate >= 80 ? 'analytics-badge-success' :
                          partner.delivery_rate >= 50 ? 'analytics-badge-warning' : 'analytics-badge-danger'
                        }>
                          {partner.delivery_rate.toFixed(1)}%
                        </Badge>
                      </td>
                      <td>
                        {partner.average_delivery_days > 0
                          ? <span>{partner.average_delivery_days}j</span>
                          : <span className="analytics-table-na">—</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      {/* Payment Methods Analysis */}
      <PaymentMethodChart />

    </ProfessionalLayout>
  );
};

export default AnalyticsDashboard;
